import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  InteractionRequiredAuthError,
  type PopupRequest,
} from '@azure/msal-browser';
import { isPlainObject, isEmpty, transform } from 'lodash';
import { Parser } from '@json2csv/plainjs';
import fileDownload from 'js-file-download';
import useToast from '@/stores/toast';
import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { JIRATransformer } from '@atlaskit/editor-jira-transformer';

import { msalInstance } from '@/config/msalConfig';
import type { IObject } from '@/types';

let isReauthenticationRedirectInProgress = false;

const getCurrentAccount = () =>
  msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

const isNoAccountError = (error: unknown): error is { errorCode: string } => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'errorCode' in error &&
    error.errorCode === 'no_account_error'
  );
};

export const triggerReauthentication = async (request: PopupRequest) => {
  if (isReauthenticationRedirectInProgress) {
    return;
  }

  isReauthenticationRedirectInProgress = true;

  try {
    const activeAccount = getCurrentAccount();

    if (!activeAccount) {
      await msalInstance.loginRedirect(request);
      return;
    }

    await msalInstance.acquireTokenRedirect({
      ...request,
      account: activeAccount,
    });
  } catch (error) {
    isReauthenticationRedirectInProgress = false;
    throw error;
  }
};

export const getToken = async (request: PopupRequest) => {
  const activeAccount = getCurrentAccount();

  if (!activeAccount) {
    await triggerReauthentication(request);
    throw new Error('Reauthentication required');
  }

  const tokenRequest = {
    ...request,
    account: activeAccount,
  };

  try {
    const res = await msalInstance.acquireTokenSilent(tokenRequest);
    return res.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError || isNoAccountError(error)) {
      await triggerReauthentication(request);
      throw new Error('Reauthentication required');
    }

    console.error(error);
    throw error;
  }
};

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

export const clean = <T extends IObject>(
  object: T,
  exceptions?: Array<string>,
) => {
  return transform<T, Partial<T>>(object, (result, value, key) => {
    // Check if the current key is listed in the exceptions array.
    // If it is, retain the original value in the result object and exit the current iteration.
    if (exceptions?.includes(key as string)) {
      result[key as keyof typeof result] = value;
      return;
    }
    // If the value is an array or an object, recursively apply the clean function.
    // This ensures that nested structures are also processed to remove unwanted values.
    if (Array.isArray(value) || isPlainObject(value)) {
      value = clean(value as IObject, exceptions) as T[keyof T];
    }

    // Exclude empty objects.
    if (isPlainObject(value) && isEmpty(value)) {
      return;
    }

    // Exclude empty arrays.
    if (Array.isArray(value) && !value.length) {
      return;
    }

    // Exclude empty strings.
    if (value === '') {
      return;
    }

    // Exclude NaN values.
    if (Number.isNaN(value)) {
      return;
    }

    // Exclude null values.
    if (value === null) {
      return;
    }

    // Exclude undefined values.
    if (value === undefined) {
      return;
    }

    // Append when recursing arrays.
    if (Array.isArray(result)) {
      return result.push(value);
    }

    result[key as keyof typeof result] = value;
  });
};

export const userLocaleTimezone = () => {
  const offset = new Date().getTimezoneOffset();
  const hours = Math.abs(Math.floor(offset / 60));
  const minutes = Math.abs(offset % 60);
  const sign = offset > 0 ? '-' : '+';

  return `UTC${sign}${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`;
};

export const exportToCsv = ({
  options,
  rows,
  fileName,
}: {
  options: object;
  rows: object[];
  fileName: string;
}) => {
  return new Promise((resolve, reject) => {
    try {
      const opts = {} as IObject;
      const parser = new Parser({ ...opts, ...options });
      const csv = parser.parse(rows as unknown as IObject[]);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      fileDownload(blob, `${fileName}.csv`);
      resolve(true);
    } catch (err) {
      console.error(err);
      reject(err);
    }
  });
};

export const titleToKey = (title: string) => {
  return title.split(' ').join('_').toLowerCase();
};

export const copyToClipboard = (text: string) => {
  if (navigator.clipboard) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log('Text copied to clipboard');
      })
      .catch((error) => {
        console.error('Failed to copy text to clipboard:', error);
      });
  } else {
    // Fallback for browsers that do not support the Clipboard API
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        console.log('Text copied to clipboard');
      } else {
        console.error('Failed to copy text to clipboard');
      }
    } catch (error) {
      console.error('Failed to copy text to clipboard:', error);
    }
    document.body.removeChild(textarea);
  }
  useToast.getState().showToast('success', {
    title: 'Copied to clipboard',
  });
};

export const isUrl = (val: string) => {
  const urlRegex =
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i;
  return urlRegex.test(val);
};

export const isJSON = (str: string) => {
  if (!str) {
    return false;
  }
  if (/^\s*(\{|\[)/.test(str)) {
    try {
      return JSON.parse(str);
      // do something here, or return obj/true
    } catch {
      return false;
      //  do nothing or return false
    }
  }
};

const jiraTransformer = new JIRATransformer(defaultSchema);
const adfTransformer = new JSONTransformer();

export const getADF = (html: string) => {
  try {
    const pmNode = jiraTransformer.parse(html);
    return adfTransformer.encode(pmNode);
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const getDomainFromUrl = (url: string) => {
  const domain = url.match(/:\/\/(www[0-9]?\.)?(.[^/:]+)/i);
  if (domain != null && domain.length > 2 && typeof domain[2] === 'string') {
    return domain[2];
  }
  return null;
};
